/**
 * Förhandlingsordning på bostadshyresmarknaden — two-part and three-part.
 *
 * The three-part inserts a fastighetsägarförening as point 2, so every
 * point from "Omfattning och undantag" on is the two-part number + 1
 * (innebörd 4 → 5, Avtalstid 13 → 14, Ny förhandlingsordning 14 → 15) and
 * Förhandlingsskyldighet cites "punkten 5 a–c". The assembler numbers the
 * points; only the cited innebörd number is computed here, because the
 * citation prints a bare "5" that a point-style `sectionRef` ("5.") cannot.
 * Every point, party points included, uses the compact clause heading.
 */
import { COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { signingPartiesFor } from '../common/index.js';
/** Clause spine after Omfattning, in order; Avtalstid sits after hyressättningsavgift. */
const CLAUSE_SPINE = [
    { instanceId: 'innebord', blockRef: 'fo.innebord@v1' },
    { instanceId: 'forhandlingsskyldighet', blockRef: 'fo.forhandlingsskyldighet@v1' },
    { instanceId: 'foreningsratten', blockRef: 'fo.foreningsratten@v1' },
    { instanceId: 'framstallning', blockRef: 'fo.framstallning@v1' },
    { instanceId: 'sammantrade', blockRef: 'fo.sammantrade-protokoll@v1' },
    { instanceId: 'overenskommelse', blockRef: 'fo.overenskommelse@v1' },
    { instanceId: 'tvistlosning', blockRef: 'fo.tvistlosning@v1' },
    { instanceId: 'strandning', blockRef: 'fo.strandning@v1' },
    { instanceId: 'hyressattningsavgift', blockRef: 'fo.hyressattningsavgift@v1' },
    { instanceId: 'avtalstid', blockRef: 'fo.avtalstid@v1' },
    { instanceId: 'ny-forhandlingsordning', blockRef: 'fo.ny-forhandlingsordning@v1' },
];
const LESSOR = { id: 'lessor', label: 'Hyresvärdspart', role: 'lessor', ordinal: 0, required: true, signs: true };
const TENANT_ORG = {
    id: 'tenant_org',
    label: 'Hyresgästorganisation',
    role: 'lessee',
    ordinal: 0,
    required: true,
    signs: true,
    kind: 'organisation',
    help: 'Organisationen som företräder hyresgästerna i förhandlingsordningen.',
};
const PROPERTY_ASSOC = {
    id: 'property_assoc',
    label: 'Fastighetsägarförening',
    role: 'other',
    ordinal: 0,
    required: true,
    signs: true,
    kind: 'organisation',
    help: 'Den fastighetsägarförening som företräder hyresvärdsparten i trepartsförhandlingen.',
};
const PART_LESSOR = { instanceId: 'part-lessor', blockRef: 'forhandling.part-hyresvardspart@v1', ref: 'fo-part-hyresvard', title: 'Hyresvärdspart' };
const PART_ASSOC = {
    instanceId: 'part-property-assoc',
    blockRef: 'forhandling.part-fastighetsagarforening@v1',
    ref: 'fo-part-fastighetsagarforening',
    title: 'Fastighetsägarförening',
};
const PART_TENANT_ORG = {
    instanceId: 'part-tenant-org',
    blockRef: 'forhandling.part-hyresgastorganisation@v1',
    ref: 'fo-part-hyresgastorg',
    title: 'Hyresgästorganisation',
};
const TWO_PART = {
    documentKey: 'forhandlingsordning_2part',
    documentVersion: 'v1',
    title: 'Förhandlingsordning på bostadshyresmarknaden · Version 2026',
    shortTitle: 'Förhandlingsordning — tvåparts',
    formNumber: 'Formulär R-03',
    category: 'Bostadshyressättningsavtal',
    partySlots: [LESSOR, TENANT_ORG],
    partyPoints: [PART_LESSOR, PART_TENANT_ORG],
};
const THREE_PART = {
    documentKey: 'forhandlingsordning_3part',
    documentVersion: 'v1',
    title: 'Förhandlingsordning på bostadshyresmarknaden – trepartsavtal · Version 2026',
    shortTitle: 'Förhandlingsordning — trepartare',
    formNumber: 'Formulär R-03B',
    category: 'Bostadshyressättningsavtal-3part',
    partySlots: [LESSOR, PROPERTY_ASSOC, TENANT_ORG],
    partyPoints: [PART_LESSOR, PART_ASSOC, PART_TENANT_ORG],
};
const PREAMBLE = 'Undertecknande parter har denna dag träffat följande överenskommelse.';
/**
 * The legacy party grid: the last two points share a row; with three
 * parties the first spans the page with its rows paired.
 */
export const partyPointLayout = (index, count) => ({
    ...(count > 2 && index === 0 ? { columns: 2 } : {}),
    ...(index === count - 1 && count > 1 ? { beside_previous: true } : {}),
});
function build(variant, values = {}) {
    const partyCount = variant.partyPoints.length;
    // Party points, then Omfattning, then innebörd.
    const innebordNumber = partyCount + 2;
    return {
        schema: COMPOSITION_SCHEMA,
        version: COMPOSITION_VERSION,
        documentKey: variant.documentKey,
        documentVersion: variant.documentVersion,
        title: variant.title,
        locale: 'sv-SE',
        meta: {
            category: variant.category,
            numberStyle: 'point',
            shortTitle: variant.shortTitle,
            formNumber: variant.formNumber,
            statute: 'hyresförhandlingslagen (1978:304)',
            signingMode: 'digital',
            partySlots: variant.partySlots,
            propertySlots: [
                {
                    id: 'fastigheter',
                    label: 'Fastigheter',
                    multi: true,
                    required: true,
                    help: 'Lägg till alla fastigheter som förhandlingsordningen omfattar.',
                },
            ],
            preamble: PREAMBLE,
        },
        values,
        blocks: [
            {
                instanceId: 'letterhead',
                blockRef: 'common.letterhead@v1',
                slot: 'letterhead',
                input: { title: variant.title, form_number: variant.formNumber },
            },
            {
                instanceId: 'footer',
                blockRef: 'common.page-footer@v1',
                slot: 'footer',
                input: { form_number: variant.formNumber },
            },
            { instanceId: 'preamble', blockRef: 'common.preamble@v1', input: { text: PREAMBLE } },
            ...variant.partyPoints.map((point, index) => ({
                instanceId: point.instanceId,
                blockRef: point.blockRef,
                input: { ref: point.ref, title: point.title, variant: 'clause', ...partyPointLayout(index, partyCount) },
            })),
            { instanceId: 'omfattning', blockRef: 'fo.omfattning@v1', input: {} },
            ...CLAUSE_SPINE.map((clause) => ({
                instanceId: clause.instanceId,
                blockRef: clause.blockRef,
                input: clause.instanceId === 'forhandlingsskyldighet' ? { innebord_number: String(innebordNumber) } : {},
            })),
            {
                instanceId: 'underskrifter',
                blockRef: 'forhandling.signing@v1',
                // Signing panels follow the party slots, in the legacy SigningBlock order.
                input: { statement: true, parties: signingPartiesFor(variant.partySlots) },
            },
        ],
    };
}
export const forhandlingsordning2PartV1 = (values = {}) => build(TWO_PART, values);
export const forhandlingsordning3PartV1 = (values = {}) => build(THREE_PART, values);
//# sourceMappingURL=forhandlingsordning.js.map